import { useCallback, useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import RiveCloudMascot from './RiveCloudMascot';
import logoSvg from '../../assets/splash/Logo.svg';
import starSvg from '../../assets/splash/Star.svg';
import topDecorSvg from '../../assets/splash/top-decor.svg';
import leftDecorSvg from '../../assets/splash/left-decor.svg';
import rightDecorSvg from '../../assets/splash/right-decor.svg';
import gradientSvg from '../../assets/splash/gradient.svg';
import { useHtmlBackground } from '../hooks/useHtmlBackground';

interface SplashV2Props {
  onComplete: () => void;
  introDelay?: number;
  fallbackTimeout?: number;
}

export default function SplashV2({
  onComplete,
  introDelay = 3000,
  fallbackTimeout = 8000,
}: SplashV2Props) {
  const introTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completedRef = useRef(false);
  const reducedMotion = useReducedMotion();
  useHtmlBackground('#DBF7F8')

  const complete = useCallback((_source: string) => {
    if (completedRef.current) return;
    completedRef.current = true;
    if (introTimerRef.current) clearTimeout(introTimerRef.current);
    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    fallbackTimerRef.current = setTimeout(() => complete('fallback'), fallbackTimeout);
    return () => {
      if (introTimerRef.current) clearTimeout(introTimerRef.current);
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
  }, [complete, fallbackTimeout]);

  const handleStateChange = useCallback((stateName: string) => {
    if (stateName === 'Intro' && !completedRef.current && !introTimerRef.current) {
      introTimerRef.current = setTimeout(() => complete('intro'), introDelay);
    }
  }, [complete, introDelay]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-50 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #00A3D4 13%, #06A9C9 27.6%, #DBF7F8 66.3%)',
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Loading splash screen"
    >
      {/* Gradient overlay - page-level background layer */}
      <img
        src={gradientSvg}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />

      <div className="max-w-[430px] mx-auto w-full h-full flex flex-col items-center overflow-hidden">
        {/* Top spacer */}
        <div className="h-[6.6%]" />

        {/* Header section */}
        <motion.div
          initial={{ opacity: 0, y: reducedMotion ? 0 : -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="relative w-full h-[176px]"
        >
          {/* Header content */}
          <div className="flex flex-col items-center justify-center h-full gap-2">
            <p
              className="text-center font-bold text-splash-lg md:text-[26px] lg:text-[28px]"
              style={{
                color: '#1B2831',
                letterSpacing: '-0.6px',
              }}
            >
              Big futures start with little
            </p>
            <img
              src={logoSvg}
              alt="Play Moments"
              className="w-[75.8%] max-w-[326px] h-auto"
            />
          </div>

          {/* Decorations */}
          <img
            src={starSvg}
            alt=""
            aria-hidden="true"
            className="absolute right-[3.7%] top-[-1.7%] w-[14.3%] max-w-[70px]"
          />
          <img
            src={topDecorSvg}
            alt=""
            aria-hidden="true"
            className="absolute left-[12.1%] top-[-8.3%] w-[67.5%] max-w-[299px]"
          />
          <img
            src={leftDecorSvg}
            alt=""
            aria-hidden="true"
            className="absolute left-[2.7%] top-[42.8%] w-[32.6%] max-w-[140px]"
          />
          <img
            src={rightDecorSvg}
            alt=""
            aria-hidden="true"
            className="absolute left-[75%] top-[35.3%] w-[27.2%] max-w-[117px]"
          />
        </motion.div>

        {/* Cloud + Message section */}
        <motion.div
          initial={{ opacity: 0, scale: reducedMotion ? 1 : 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="flex-1 flex flex-col items-center 
          justify-center relative w-full min-h-[180px] 
          overflow-hidden gap-2 pb-[24%]"
        >
          <RiveCloudMascot
            className="relative w-[70.7%] max-w-[304px] aspect-square"
            onStateChange={handleStateChange}
          />
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="relative text-center text-[16px] md:text-[18px] lg:text-[20px] w-[55%] max-w-[240px]"
            style={{ color: '#1B2831' }}
          >
            Take a tiny pause while we&apos;re getting today&apos;s Sparks ready
          </motion.p>
        </motion.div>
      </div>
    </motion.div>
  );
}
